import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import TablePagination from "../Utils/TablePagination";
import {
  ClipboardList,
  CheckCircle,
  XCircle,
  IndianRupee,
  Wallet,
  FileDown,
  ArrowDown,
  ArrowUp,
  CalendarDays,
  Sparkles,
  Clock,
  ThumbsUp,
  ThumbsDown,
  MapPin,
  Check,
  X,
  ChevronUp,
  ChevronDown,
} from "lucide-react";

import { exportAnyJsonToExcel } from "../Utils/exportAnyJsonToExcel";
import useReports from "../../Sevices/useReports";
import { Doughnut, Bar } from "react-chartjs-2";
import { useLocation } from "react-router-dom";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from "chart.js";

ChartJS.register(ArcElement, Tooltip, Legend);

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

// --------- FORMATTERS ---------
const fmt2 = (num) =>
  Number(num || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatMinutes = (minutes) => {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes === 0
    ? `${hours} h`
    : `${hours} h ${remainingMinutes} min`;
};

const formatDistance = (meters) => {
  if (!meters || isNaN(meters)) return "N/A";
  return meters < 1000
    ? `${Math.round(meters)} m`
    : `${(meters / 1000).toFixed(2)} km`;
};

const formatTime = (datetime) => {
  if (!datetime) return "N/A";
  return new Date(datetime).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

// --------- BADGE COMPONENT ---------
const Badge = ({ children, className = "", title }) => (
  <span
    title={title}
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${className}`}
  >
    {children}
  </span>
);

// --------- HELPERS (same as your sample UI) ---------
const parseEstimatedMinutes = (estimated) => {
  if (!estimated) return null;
  const m = String(estimated).toLowerCase().match(/(\d+)/);
  return m ? Number(m[1]) : null;
};

const normalizeTaskUpdate = (v) =>
  String(v || "")
    .trim()
    .toLowerCase();

const getWaitingMeta = (waiting) => {
  const w = String(waiting || "")
    .trim()
    .toLowerCase();
  if (!w) return { kind: "none", text: "No waiting record found" };

  if (w.startsWith("restaurant waited"))
    return { kind: "against_driver", text: waiting };
  if (w.startsWith("driver waited"))
    return { kind: "driver_favor", text: waiting };

  return { kind: "neutral", text: waiting };
};

// --------- CELL RENDERERS (UI copied from your design) ---------
const renderDeliveryTimeCell = (task) => {
  const deliveryMin = Number(task?.foodIsReadytoPickedup);
  const estMin = parseEstimatedMinutes(task?.foodIsReadytoPickedup);

  if (!Number.isFinite(deliveryMin)) return "N/A";

  const isLate = Number.isFinite(estMin) && deliveryMin > estMin;

  if (isLate) {
    return (
      <Badge
        title={`Late: ${deliveryMin} min vs est ${estMin} min`}
        className="bg-red-50 text-red-700 border border-red-200"
      >
        <ArrowDown size={14} />
        <span>{formatMinutes(deliveryMin)}</span>
      </Badge>
    );
  }

  return (
    <Badge
      title={
        Number.isFinite(estMin)
          ? `On time: ${deliveryMin} min vs est ${estMin} min`
          : `Delivery: ${deliveryMin} min`
      }
      className="bg-green-50 text-green-700 border border-green-200"
    >
      <ArrowUp size={14} />
      <span>{formatMinutes(deliveryMin)}</span>
    </Badge>
  );
};

function getViolationType(task) {
  const estimatedTimeMissing =
    task.estimated_time === null ||
    task.estimated_time === undefined ||
    task.estimated_time === "";

  const foodReadyToSuccess =
    task.foodIsReadytoSuccessful === null ||
      task.foodIsReadytoSuccessful === undefined ||
      task.foodIsReadytoSuccessful === ""
      ? null
      : Number(task.foodIsReadytoSuccessful);

  const pickupToSuccess =
    task.pickeduptoSuccessful === null ||
      task.pickeduptoSuccessful === undefined ||
      task.pickeduptoSuccessful === ""
      ? null
      : Number(task.pickeduptoSuccessful);

  //   Driver Location Off (Highest Priority)
  if (estimatedTimeMissing) {
    return "Driver Location Off";
  }

  //  Instant Delivery
  if (pickupToSuccess === 0) {
    return "Instant Delivery";
  }

  //  Pickup Missed
  if (foodReadyToSuccess !== null && pickupToSuccess === null) {
    return "Pickup Missed";
  }

  //  Restro Missed
  if (foodReadyToSuccess === null) {
    return "Restro Missed";
  }

  return "Normal";
}

const renderTaskUpdateCell = (taskUpdate) => {
  const t = normalizeTaskUpdate(taskUpdate);

  if (t === "daily") {
    return (
      <Badge className="bg-transparent text-blue-700 border border-blue-300">
        <CalendarDays size={14} />
        <span>Daily</span>
      </Badge>
    );
  }

  if (t === "extra") {
    return (
      <Badge className="bg-green-50 text-green-700 border border-green-200">
        <Sparkles size={14} />
        <span>Extra</span>
      </Badge>
    );
  }

  return (
    <Badge className=" text-gray-700 border border-gray-200">
      <Clock size={14} />
      <span>{taskUpdate ?? "N/A"}</span>
    </Badge>
  );
};

const renderWaitingTimeCell = (waiting_time) => {
  const meta = getWaitingMeta(waiting_time);

  if (meta.kind === "driver_favor") {
    return (
      <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">
        <ThumbsUp size={14} />
        <span>{meta.text}</span>
      </Badge>
    );
  }

  if (meta.kind === "against_driver") {
    return (
      <Badge className="bg-amber-50 text-amber-800 border border-amber-200">
        <ThumbsDown size={14} />
        <span>{meta.text}</span>
      </Badge>
    );
  }

  if (meta.kind === "none")
    return <span className="text-gray-400">{meta.text}</span>;

  return (
    <Badge className=" text-gray-700 border border-gray-200">
      <Clock size={14} />
      <span>{meta.text}</span>
    </Badge>
  );
};

export default function DriverAnalytics() {

  const { driverId, username } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const today = new Date().toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(
    location.state?.fromDate || today
  );

  const [toDate, setToDate] = useState(
    location.state?.toDate || today
  );

  const { fetchDriverAnalytics, fetchDriverReports, loading, taskLoading } =
    useReports();

  const [analytics, setAnalytics] = useState(null);
  const [reports, setReports] = useState([]);

  const reportsPerPage = 15;
  const [currentPage, setCurrentPage] = useState(1);

  const totalCount = reports.length;
  const indexOfLast = currentPage * reportsPerPage;
  const indexOfFirst = indexOfLast - reportsPerPage;
  const currentReports = reports.slice(indexOfFirst, indexOfLast);

  const [activeTab, setActiveTab] = useState("analytics");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("ontime");
  const analyticsPerPage = 15;
  const [analyticsPage, setAnalyticsPage] = useState(1);

  const [expandedRows, setExpandedRows] = useState({});

  const toggleRow = (taskId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  };

  const deliveryComparisonData = {
    labels: reports.map((task) => `Task ${task.pk_task_id}`),

    datasets: [
      {
        label: "Estimated Time (min)",
        data: reports.map((task) => Number(task.actual_estimated_time)),
        backgroundColor: "#3b82f6",
      },
      {
        label: "Actual Delivery Time (min)",
        data: reports.map((task) => Number(task.pickeduptoSuccessful)),
        backgroundColor: "#ef4444",
      },
    ],
  };

  const locationOffCount = reports.filter(
    (task) => getViolationType(task) === "Driver Location Off",
  ).length;

  const instantCount = reports.filter(
    (task) => getViolationType(task) === "Instant Delivery",
  ).length;

  const pickupMissedCount = reports.filter(
    (task) => getViolationType(task) === "Pickup Missed",
  ).length;

  const restroMissedCount = reports.filter(
    (task) => getViolationType(task) === "Restro Missed",
  ).length;

  const normalCount = reports.filter(
    (task) => getViolationType(task) === "Normal",
  ).length;

  const handleApply = () => {
    setCurrentPage(1);
    fetchDriverAnalytics(driverId, fromDate, toDate, setAnalytics);
    fetchDriverReports(driverId, fromDate, toDate, setReports);
  };

  const handleExport = () => {
    if (!reports.length) {
      alert("No reports available to export");
      return;
    }

    exportAnyJsonToExcel({
      data: reports,
      filePrefix: `Driver_Report_${driverId}`,
      sheetName: "Driver Reports",
    });
  };

  const showValue = (v, isAmount = false) => {
    if (v === null || v === undefined) return "N/A";
    return isAmount ? `₹ ${fmt2(v)}` : v;
  };

  const filteredReports = reports.filter((task) => {
    // Convert times properly
    const actual = Number(task.actual_estimated_time);
    const estimated = Number(task.estimated_time?.replace(/\D/g, ""));

    const isOnTime = actual <= estimated;
    const isLate = actual > estimated;

    if (statusFilter === "ontime" && !isOnTime) return false;
    if (statusFilter === "late" && !isLate) return false;

    if (selectedDate && task.delivery_date !== selectedDate) return false;

    const searchLower = searchTerm.toLowerCase();

    if (
      searchTerm &&
      !(
        task.pk_task_id?.toString().includes(searchLower) ||
        task.order_id?.toString().includes(searchLower) ||
        task.task_description?.toLowerCase().includes(searchLower)
      )
    )
      return false;

    return true;
  });

  const onTimeTasks = filteredReports.filter((task) => {
    if (!task.actual_estimated_time || !task.estimated_time) return false;

    const actual = Number(task.actual_estimated_time);
    const estimated = Number(task.estimated_time.replace(/\D/g, ""));

    return actual <= estimated;
  });

  const lateTasks = filteredReports.filter((task) => {
    if (!task.actual_estimated_time || !task.estimated_time) return false;

    const actual = Number(task.actual_estimated_time);
    const estimated = Number(task.estimated_time.replace(/\D/g, ""));

    return actual > estimated;
  });

  const notDeliveredTasks = filteredReports.filter(
    (task) => task.actual_estimated_time === null,
  );

  const statusBasedReports = filteredReports.filter((task) => {
    const actual = Number(task.actual_estimated_time);
    const estimated = Number(task.estimated_time?.replace(/\D/g, ""));

    switch (selectedStatus) {
      case "ontime":
        return (
          task.actual_estimated_time !== null &&
          task.actual_estimated_time !== undefined &&
          task.actual_estimated_time !== "" &&
          Number.isFinite(actual) &&
          Number.isFinite(estimated) &&
          actual <= estimated
        );

      case "late":
        return (
          task.actual_estimated_time !== null &&
          task.actual_estimated_time !== undefined &&
          task.actual_estimated_time !== "" &&
          Number.isFinite(actual) &&
          Number.isFinite(estimated) &&
          actual > estimated
        );
      case "notdelivered":
        return task.actual_estimated_time === null;

      case "locationoff":
        return getViolationType(task) === "Driver Location Off";

      case "instant":
        return getViolationType(task) === "Instant Delivery";

      case "pickupmissed":
        return getViolationType(task) === "Pickup Missed";

      case "restromissed":
        return getViolationType(task) === "Restro Missed";

      case "normal":
        return getViolationType(task) === "Normal";

      default:
        return true;
    }
  });

  const indexOfLastAnalytics = analyticsPage * analyticsPerPage;

  const indexOfFirstAnalytics = indexOfLastAnalytics - analyticsPerPage;

  const currentAnalyticsReports = statusBasedReports.slice(
    indexOfFirstAnalytics,
    indexOfLastAnalytics,
  );

  const StatusCard = ({ title, color, value, active, icon, onClick }) => {
    return (
      <div
        onClick={onClick}
        className={`
        cursor-pointer rounded-2xl p-5 transition-all duration-300
        border-l-4 ${color.border}
        ${color.bg}
        ${active ? `border-2 ${color.border} shadow-lg` : "border border-transparent shadow"}
        hover:shadow-md
      `}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${color.iconBg}`}>{icon}</div>

          <div>
            <p className="text-sm text-gray-600">{title}</p>
            <h3 className={`text-2xl font-bold ${color.text}`}>{value}</h3>
          </div>
        </div>
      </div>
    );
  };

  const onTimeCount = filteredReports.filter((task) => {
    if (!task.actual_estimated_time || !task.estimated_time) return false;

    const actualTime = Number(task.actual_estimated_time);
    const estimatedTime = Number(task.estimated_time.replace(/\D/g, ""));

    return actualTime <= estimatedTime;
  }).length;

  const lateCount = reports.filter((task) => {
    if (!task.actual_estimated_time || !task.estimated_time) return false;

    const actualTime = Number(task.actual_estimated_time);
    const estimatedTime = Number(task.estimated_time.replace(/\D/g, ""));

    return actualTime > estimatedTime;
  }).length;

  const notDeliveredCount = reports.filter(
    (task) => !task.actual_estimated_time,
  ).length;

  const deliveryPieData = {
    labels: [
      "On Time",
      "Late",
      "Driver Location Off",
      "Instant Delivery",
      "Pickup Missed",
      "Restro Missed",
      "Normal",
    ],
    datasets: [
      {
        data: [
          onTimeCount,
          lateCount,
          locationOffCount,
          instantCount,
          pickupMissedCount,
          restroMissedCount,
          normalCount,
        ],
        backgroundColor: [
          "#16a34a", // On Time - emerald
          "#dc2626", // Late - crimson
          "#d97706", // Driver Location Off - amber
          "#fb923c", // Instant - cyan
          "#7c3aed", // Pickup Missed - violet
          "#be123c", // Restro Missed - rose
          "#4f46e5", // Normal - indigo
        ],
        borderWidth: 1,
      },
    ],
  };

  useEffect(() => {
    setAnalyticsPage(1);
  }, [selectedStatus]);

  useEffect(() => {
    if (driverId && fromDate && toDate) {
      fetchDriverAnalytics(driverId, fromDate, toDate, setAnalytics);
      fetchDriverReports(driverId, fromDate, toDate, setReports);
    }
  }, [driverId]);

  return (
    <div className="p-6 min-h-screen space-y-5 ">
      {/* HEADER + FILTERS */}
      <div className="flex flex-col md:flex-row md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">
            Packky Driver Analytics — {decodeURIComponent(username || "")}
            <span className="text-gray-400"> ({driverId})</span>
          </h2>
          <p className="text-sm text-gray-500">
            Filter, analyze, process & export
          </p>
        </div>

        <div className="flex flex-wrap gap-3 items-end">
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="border p-2 rounded text-sm"
          />
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="border p-2 rounded text-sm"
          />

          <button
            onClick={handleApply}
            className="px-4 py-2 bg-blue-600 text-white rounded"
          >
            Apply
          </button>

          <button
            onClick={handleExport}
            className="px-4 py-2 bg-green-600 text-white rounded flex items-center gap-2"
          >
            <FileDown size={16} /> Export
          </button>

          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 border rounded"
          >
            ← Back
          </button>
        </div>
      </div>

      {/* ANALYTICS CARDS (New Styled UI) */}
      {loading && <p>Loading analytics...</p>}

      {!loading && analytics && (
        <div className="grid gap-3 md:grid-cols-5">
          {[
            {
              label: "Assigned Orders",
              value: analytics.assigned_orders,
              icon: <ClipboardList className="h-6 w-6 text-blue-600" />,
              border: "border-l-blue-500",
              bg: "bg-blue-50",
              hover: "hover:bg-blue-100",
            },
            {
              label: "Successful Orders",
              value: analytics.current_successful_orders,
              icon: <CheckCircle className="h-6 w-6 text-green-600" />,
              border: "border-l-green-500",
              bg: "bg-green-50",
              hover: "hover:bg-green-100",
            },
            {
              label: "Declined Orders",
              value: analytics.declined_orders,
              icon: <XCircle className="h-6 w-6 text-red-600" />,
              border: "border-l-red-500",
              bg: "bg-red-50",
              hover: "hover:bg-red-100",
            },
            {
              label: "Per Day Earning",
              value: showValue(analytics.per_day, true),
              icon: <IndianRupee className="h-6 w-6 text-indigo-600" />,
              border: "border-l-indigo-500",
              bg: "bg-indigo-50",
              hover: "hover:bg-indigo-100",
            },
            {
              label: "Per Order Earning",
              value: showValue(analytics.per_order, true),
              icon: <Wallet className="h-6 w-6 text-emerald-600" />,
              border: "border-l-emerald-500",
              bg: "bg-emerald-50",
              hover: "hover:bg-emerald-100",
            },
          ].map((c, i) => (
            <div
              key={i}
              className={`group rounded-2xl border-l-4 ${c.border} ${c.bg} ${c.hover} p-4 transition-all duration-300`}
            >
              <div className="flex items-center gap-3">
                {c.icon}
                <div>
                  <div className="text-xs text-gray-600">{c.label}</div>
                  <div className="text-lg font-semibold text-gray-800">
                    {c.value}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* TABS */}
      <div className="border-b border-gray-200 mt-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab("analytics")}
            className={`py-3 px-1 border-b-2 text-sm font-medium ${activeTab === "analytics"
                ? "border-orange-600 text-orange-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
          >
            Analytics
          </button>

          <button
            onClick={() => setActiveTab("reports")}
            className={`py-3 px-1 border-b-2 text-sm font-medium ${activeTab === "reports"
                ? "border-orange-600 text-orange-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
          >
            Reports
          </button>
        </nav>
      </div>

      {activeTab === "analytics" && reports.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* BAR CHART */}
          <div className="bg-white p-4 rounded shadow">
            <h3 className="text-lg font-semibold mb-4">
              Estimated vs Actual Delivery Time
            </h3>

            <div className="h-80">
              <Bar
                data={deliveryComparisonData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                }}
              />
            </div>
          </div>

          {/* PIE CHART */}
          <div className="bg-white p-4 rounded shadow">
            <h3 className="text-lg font-semibold mb-4">
              Delivery Performance Distribution
            </h3>

            <div className="flex items-center justify-between h-80">
              {/* LEFT SIDE → DOUGHNUT */}
              <div className="w-1/2 h-full">
                <Doughnut
                  key={JSON.stringify(deliveryPieData)}
                  data={deliveryPieData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: "65%",
                    plugins: {
                      legend: {
                        display: false,
                      },
                    },
                  }}
                />
              </div>

              {/* RIGHT SIDE → COUNTS */}
              <div className="w-1/2 h-full overflow-y-auto pr-2 space-y-4 custom-scroll">
                {/* ================= POSITIVE SECTION ================= */}
                <div>
                  <h4 className="text-sm font-semibold text-emerald-700 mb-2">
                    Positive Performance
                  </h4>

                  <div className="space-y-2">
                    {/* On Time */}
                    <div className="relative h-10">
                      <div className="absolute left-0 top-0 h-full w-1 bg-emerald-600 rounded-l-md"></div>
                      <div className="bg-emerald-50 border border-emerald-200 rounded-md px-4 h-full flex items-center justify-between shadow-sm">
                        <span className="text-sm font-medium text-emerald-700">
                          On Time
                        </span>
                        <span className="text-lg font-semibold text-emerald-700">
                          {onTimeCount}
                        </span>
                      </div>
                    </div>

                    {/* Late */}
                    <div className="relative h-10">
                      <div className="absolute left-0 top-0 h-full w-1 bg-red-600 rounded-l-md"></div>
                      <div className="bg-red-50 border border-red-200 rounded-md px-4 h-full flex items-center justify-between shadow-sm">
                        <span className="text-sm font-medium text-red-700">
                          Late
                        </span>
                        <span className="text-lg font-semibold text-red-700">
                          {lateCount}
                        </span>
                      </div>
                    </div>

                    {/* Normal */}
                    <div className="relative h-10">
                      <div className="absolute left-0 top-0 h-full w-1 bg-indigo-600 rounded-l-md"></div>
                      <div className="bg-indigo-50 border border-indigo-200 rounded-md px-4 h-full flex items-center justify-between shadow-sm">
                        <span className="text-sm font-medium text-indigo-700">
                          Normal
                        </span>
                        <span className="text-lg font-semibold text-indigo-700">
                          {normalCount}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ================= NEGATIVE SECTION ================= */}
                <div>
                  <h4 className="text-sm font-semibold text-red-700 mb-2">
                    Misslianus / Violations
                  </h4>

                  <div className="space-y-2">
                    {/* Driver Location Off */}
                    <div className="relative h-10">
                      <div className="absolute left-0 top-0 h-full w-1 bg-amber-600 rounded-l-md"></div>
                      <div className="bg-amber-50 border border-amber-200 rounded-md px-4 h-full flex items-center justify-between shadow-sm">
                        <span className="text-sm font-medium text-amber-700">
                          Driver Location Off
                        </span>
                        <span className="text-lg font-semibold text-amber-700">
                          {notDeliveredCount}
                        </span>
                      </div>
                    </div>

                    {/* Instant Delivery */}
                    <div className="relative h-10">
                      <div className="absolute left-0 top-0 h-full w-1 bg-orange-600 rounded-l-md"></div>
                      <div className="bg-orange-50 border border-orange-200 rounded-md px-4 h-full flex items-center justify-between shadow-sm">
                        <span className="text-sm font-medium text-orange-700">
                          Instant Delivery
                        </span>
                        <span className="text-lg font-semibold text-orange-700">
                          {instantCount}
                        </span>
                      </div>
                    </div>

                    {/* Pickup Missed */}
                    <div className="relative h-10">
                      <div className="absolute left-0 top-0 h-full w-1 bg-violet-600 rounded-l-md"></div>
                      <div className="bg-violet-50 border border-violet-200 rounded-md px-4 h-full flex items-center justify-between shadow-sm">
                        <span className="text-sm font-medium text-violet-700">
                          Pickup Missed
                        </span>
                        <span className="text-lg font-semibold text-violet-700">
                          {pickupMissedCount}
                        </span>
                      </div>
                    </div>

                    {/* Restro Missed */}
                    <div className="relative h-10">
                      <div className="absolute left-0 top-0 h-full w-1 bg-rose-600 rounded-l-md"></div>
                      <div className="bg-rose-50 border border-rose-200 rounded-md px-4 h-full flex items-center justify-between shadow-sm">
                        <span className="text-sm font-medium text-rose-700">
                          Restro Missed
                        </span>
                        <span className="text-lg font-semibold text-rose-700">
                          {restroMissedCount}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "analytics" && reports.length > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6 w-full">
            {/* On Time */}
            <StatusCard
              title="On Time"
              value={onTimeTasks.length}
              active={selectedStatus === "ontime"}
              onClick={() => setSelectedStatus("ontime")}
              icon={<ThumbsUp className="h-5 w-5 text-green-600" />}
              color={{
                border: "border-green-500",
                bg: "bg-green-50",
                text: "text-green-600",
                iconBg: "bg-green-100",
              }}
            />

            {/* Late */}
            <StatusCard
              title="Late"
              value={lateTasks.length}
              active={selectedStatus === "late"}
              onClick={() => setSelectedStatus("late")}
              icon={<ArrowDown className="h-5 w-5 text-red-600" />}
              color={{
                border: "border-red-500",
                bg: "bg-red-50",
                text: "text-red-600",
                iconBg: "bg-red-100",
              }}
            />

            <StatusCard
              title="Normal Delivery"
              value={normalCount}
              active={selectedStatus === "normal"}
              onClick={() => setSelectedStatus("normal")}
              icon={<CheckCircle className="h-5 w-5 text-blue-700" />}
              color={{
                border: "border-blue-600",
                bg: "bg-blue-50",
                text: "text-blue-700",
                iconBg: "bg-blue-100",
              }}
            />

            {/* Driver Location Off */}
            <StatusCard
              title="Driver Location Off"
              value={notDeliveredTasks.length}
              active={selectedStatus === "notdelivered"}
              onClick={() => setSelectedStatus("notdelivered")}
              icon={<MapPin className="h-5 w-5 text-amber-700" />}
              color={{
                border: "border-amber-600",
                bg: "bg-amber-50",
                text: "text-amber-700",
                iconBg: "bg-amber-100",
              }}
            />

            {/* Instant Delivery */}
            <StatusCard
              title="Instant Delivery"
              value={instantCount}
              active={selectedStatus === "instant"}
              onClick={() => setSelectedStatus("instant")}
              icon={<Sparkles className="h-5 w-5 text-orange-600" />}
              color={{
                border: "border-orange-500",
                bg: "bg-orange-50",
                text: "text-orange-600",
                iconBg: "bg-orange-100",
              }}
            />

            {/* Pickup Missed */}
            <StatusCard
              title="Pickup Missed"
              value={pickupMissedCount}
              active={selectedStatus === "pickupmissed"}
              onClick={() => setSelectedStatus("pickupmissed")}
              icon={<ArrowUp className="h-5 w-5 text-violet-600" />}
              color={{
                border: "border-violet-500",
                bg: "bg-violet-50",
                text: "text-violet-600",
                iconBg: "bg-violet-100",
              }}
            />

            {/* Restro Missed */}
            <StatusCard
              title="Restro Missed"
              value={restroMissedCount}
              active={selectedStatus === "restromissed"}
              onClick={() => setSelectedStatus("restromissed")}
              icon={<XCircle className="h-5 w-5 text-red-700" />}
              color={{
                border: "border-red-700",
                bg: "bg-red-100",
                text: "text-red-700",
                iconBg: "bg-red-200",
              }}
            />
          </div>

          <div className="mt-6 mb-3">
            <h2 className="text-lg font-semibold">
              {selectedStatus === "ontime" && "On Time Deliveries"}
              {selectedStatus === "late" && "Late Deliveries"}
              {selectedStatus === "notdelivered" && "Not Delivered Orders"}
              {selectedStatus === "locationoff" && "Driver Location Off Cases"}
              {selectedStatus === "instant" && "Instant Deliveries"}
              {selectedStatus === "pickupmissed" && "Pickup Missed"}
              {selectedStatus === "restromissed" && "Restro Missed"}
              {selectedStatus === "normal" && "Normal Deliveries"}
            </h2>

            <p className="text-sm text-gray-500">
              Showing {statusBasedReports.length} records
            </p>
          </div>

          <div className="bg-white border rounded-lg shadow-sm mt-4">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-orange-400 text-white text-left">
                  <tr>
                    <th className="px-4 py-2">Task Id</th>
                    <th className="px-4 py-2">Order Id</th>
                    <th className="px-4 py-2">Description</th>
                    <th className="px-4 py-2">Actual Time</th>
                    <th className="px-4 py-2">Estimate</th>
                    <th className="px-4 py-2">Distance</th>
                    <th className="px-4 py-2">Delivery Date</th>
                    <th className="px-4 py-2">Waiting</th>
                  </tr>
                </thead>

                <tbody>
                  {currentAnalyticsReports.map((task, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-4 py-2">{task.pk_task_id}</td>
                      <td className="px-4 py-2">{task.order_id}</td>
                      <td className="px-4 py-2">{task.task_description}</td>
                      <td className="px-4 py-2 d-flex align-items-center justify-center">
                        {task.actual_estimated_time
                          ? `${task.actual_estimated_time} min`
                          : "N/A"}
                      </td>
                      <td className="px-4 py-2">{task.estimated_time}</td>
                      <td className="px-4 py-2">
                        {formatDistance(task.distance)}
                      </td>
                      <td className="px-4 py-2">{task.delivery_date}</td>
                      <td className="px-4 py-2">
                        {renderWaitingTimeCell(task.waiting_time)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <TablePagination
            currentPage={analyticsPage}
            totalCount={statusBasedReports.length}
            pageSize={analyticsPerPage}
            onPageChange={setAnalyticsPage}
          />
        </>
      )}

      {activeTab === "reports" && (
        <div className="bg-white p-4 rounded shadow mb-4 flex flex-wrap gap-4 items-center">
          <input
            type="text"
            placeholder="Search by Task ID, Order ID, Description"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border px-3 py-2 rounded w-64"
          />

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border px-3 py-2 rounded"
          >
            <option value="all">All Status</option>
            <option value="ontime">On Time</option>
            <option value="late">Late</option>
          </select>
        </div>
      )}

      {activeTab === "reports" && (
        <div className="bg-white border rounded-lg shadow-sm mt-6">
          {taskLoading ? (
            <p className="p-4">Loading reports...</p>
          ) : reports.length === 0 ? (
            <p className="p-4 text-gray-400">
              No reports found for selected date range.
            </p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-orange-400 text-white text-left">
                    <tr>
                      <th className="px-4 py-2">Task Id</th>
                      <th className="px-4 py-2">Order Id</th>
                      <th className="px-4 py-2">Description</th>
                      <th className="px-4 py-2">Actual Time</th>
                      <th className="px-4 py-2">Estimate</th>
                      <th className="px-4 py-2">Distance</th>
                      <th className="px-4 py-2">Delivery Date</th>
                      <th className="px-4 py-2">Waiting</th>
                      <th className="px-4 py-2">Task Update</th>
                      <th className="px-4 py-2">Charges</th>
                      <th className="px-4 py-2">Pickup Time</th>
                      <th className="px-4 py-2">Successful Time</th>
                      <th className="px-4 py-2">Time Details</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredReports.map((task, i) => (
                      <>
                        <tr key={i} className="border-t">
                          <td className="px-4 py-2">{task.pk_task_id}</td>
                          <td className="px-4 py-2">{task.order_id}</td>
                          <td className="px-4 py-2">{task.task_description}</td>
                          <td className="px-4 py-2">
                            {task.actual_estimated_time
                              ? `${task.actual_estimated_time} min`
                              : "N/A"}
                          </td>
                          <td className="px-4 py-2">{task.estimated_time}</td>
                          <td className="px-4 py-2">
                            {formatDistance(task.distance)}
                          </td>
                          <td className="px-4 py-2">
                            {task.delivery_date ?? "N/A"}
                          </td>
                          <td className="px-4 py-2">
                            {renderWaitingTimeCell(task.waiting_time)}
                          </td>
                          <td className="px-4 py-2">
                            {renderTaskUpdateCell(task.task_update)}
                          </td>
                          <td className="px-4 py-2">₹ {fmt2(task.charges)}</td>
                          <td className="px-4 py-2">
                            {renderDeliveryTimeCell(task)}
                          </td>
                          <td className="px-4 py-2">
                            {task.pickeduptoSuccessful !== null
                              ? formatMinutes(Number(task.pickeduptoSuccessful))
                              : task.foodIsReadytoSuccessful !== null
                                ? formatMinutes(
                                  Number(task.foodIsReadytoSuccessful),
                                )
                                : "N/A"}
                          </td>
                          {/* EXPAND BUTTON */}
                          <td className="px-4 py-2">
                            <button
                              onClick={() => toggleRow(task.pk_task_id)}
                              className="px-2 py-1 text-xs bg-orange-100 text-orange-700 border border-orange-300 rounded hover:bg-orange-200 transition flex items-center gap-1"
                            >
                              {expandedRows[task.pk_task_id] ? (
                                <>
                                  <ChevronUp size={14} /> Hide
                                </>
                              ) : (
                                <>
                                  <ChevronDown size={14} /> Details
                                </>
                              )}
                            </button>
                          </td>
                        </tr>

                        {/* EXPANDED DETAIL ROW */}
                        {expandedRows[task.pk_task_id] && (
                          <tr
                            key={`detail-${i}`}
                            className=" border-t border-gray-200"
                          >
                            <td colSpan={13} className="px-6 py-6">
                              {/* TOP ROW — circles + lines */}
                              <div className="flex items-center justify-center">
                                {[
                                  {
                                    label: "Acknowledged",
                                    time: task.acknowledged_time,
                                  },
                                  { label: "Arrived", time: task.arrival_time },
                                  {
                                    label: "Food Ready",
                                    time: task.food_ready_time,
                                  },
                                  {
                                    label: "Driver Ready",
                                    time: task.driver_food_ready_time,
                                  },
                                  {
                                    label: "Picked Up",
                                    time: task.picked_up_time,
                                  },
                                  {
                                    label: "Delivered",
                                    time: task.delivery_time,
                                  },
                                ].map((step, idx, arr) => (
                                  <div key={idx} className="flex items-center">
                                    {/* Circle */}
                                    <div
                                      className={`w-10 h-10 rounded-full flex items-center justify-center text-white shadow-md flex-shrink-0
                                          ${step.time ? "bg-green-500" : "bg-red-400"}`}
                                    >
                                      {step.time ? (
                                        <Check size={18} strokeWidth={3} />
                                      ) : (
                                        <X size={18} strokeWidth={3} />
                                      )}
                                    </div>

                                    {idx < arr.length - 1 && (
                                      <div
                                        className={`h-0.5 w-24 flex-shrink-0 ${step.time && arr[idx + 1].time ? "bg-green-500" : "bg-red-300"}`}
                                      />
                                    )}
                                  </div>
                                ))}
                              </div>

                              {/* BOTTOM ROW — labels + times aligned under each circle */}
                              <div className="flex items-start justify-center mt-2">
                                {[
                                  {
                                    label: "Acknowledged",
                                    time: task.acknowledged_time,
                                  },
                                  { label: "Arrived", time: task.arrival_time },
                                  {
                                    label: "Food Ready",
                                    time: task.food_ready_time,
                                  },
                                  {
                                    label: "Driver Ready",
                                    time: task.driver_food_ready_time,
                                  },
                                  {
                                    label: "Picked Up",
                                    time: task.picked_up_time,
                                  },
                                  {
                                    label: "Delivered",
                                    time: task.delivery_time,
                                  },
                                ].map((step, idx, arr) => (
                                  <div key={idx} className="flex items-start">
                                    {/* Label + Time under circle */}
                                    <div className="flex flex-col items-center w-10 flex-shrink-0">
                                      <span className="text-xs text-gray-700 font-semibold text-center leading-tight whitespace-nowrap">
                                        {step.label}
                                      </span>
                                      <span
                                        className={`text-xs mt-0.5 text-center font-medium whitespace-nowrap ${step.time ? "text-gray-500" : "text-red-500"}`}
                                      >
                                        {formatTime(step.time)}
                                      </span>
                                    </div>

                                    {/* Spacer matching line width */}
                                    {idx < arr.length - 1 && (
                                      <div className="w-24 flex-shrink-0" />
                                    )}
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    ))}
                  </tbody>
                </table>
              </div>

              <TablePagination
                currentPage={currentPage}
                totalCount={totalCount}
                pageSize={reportsPerPage}
                onPageChange={setCurrentPage}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
